import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Users,
  Crown,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileText,
  User,
  Clock,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getWithTokenAPI } from "../../util/Api";
import { useTranslation } from "react-i18next";

const DashboardSection = () => {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportCount, setReportCount] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showAllApprovals, setShowAllApprovals] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem('matrimony_token');

  // Navigation handlers
  const handleTotalUsersClick = () => {
    navigate('/admin/user-management');
  };

  const handleActiveProfilesClick = () => {
    navigate('/admin/user-management?status=active');
  };

const handleGoldUsersClick = () => {
  navigate("/admin/payment-list?planName=Gold&subscriptionStatus=active");
};

const handleEliteUsersClick = () => {
  navigate("/admin/payment-list?planName=Elite&subscriptionStatus=active");
};

  const handleFreeUsersClick = () => {
    navigate('/admin/payment-list');
  };

const handlePendingApprovalsClick = () => {
  navigate('/admin/content-approvals?approvals=pending');
};
  const handleReportedUsersClick = () => {
    navigate('/admin/reports-moderation');
  };

  const handleMonthlyRevenueClick = () => {
    navigate('/admin/payment-list');
  };

  // NEW: Navigation handlers for Show More/Show All buttons
  const handleShowMoreUsers = () => {
    navigate('/admin/user-management');
  };

  const handleShowAllUsers = () => {
    navigate('/admin/user-management');
  };

  const handleShowMoreApprovals = () => {
    navigate('/admin/content-approvals');
  };

  const handleShowAllApprovals = () => {
    navigate('/admin/content-approvals');
  };

  // Fetch dashboard summary data
  const getSummary = async () => {
    try {
      setLoading(true);
      const response = await getWithTokenAPI(token, '/dashboard/summary');
      console.log('Dashboard Summary:', response);

      if (response?.statusCode === 200) {
        setSummaryData(response.data);
      } else {
        // Fallback data if API fails
        setSummaryData({
          totalUsers: 95,
          activeProfiles: 63,
          goldUsers: 2,
          monthlyRevenue: 30992,
          eliteUsers: 4,
          freeUsers: 89
        });
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      // Set fallback data
      setSummaryData({
        totalUsers: 95,
        activeProfiles: 63,
        goldUsers: 2,
        monthlyRevenue: 30992,
        eliteUsers: 4,
        freeUsers: 89
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch reports count
  const getReports = async () => {
    try {
      const response = await getWithTokenAPI(token, '/reports/get_all');
      console.log('Reports:', response);

      if (response?.statusCode === 200) {
        // Handle different response structures
        if (response.data?.totalCount !== undefined) {
          setReportCount(response.data.totalCount);
        } else if (Array.isArray(response.data)) {
          setReportCount(response.data.length);
        } else if (response.data?.reports) {
          setReportCount(response.data.reports.length);
        } else {
          setReportCount(16); // Fallback
        }
      } else {
        setReportCount(16); // Fallback
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReportCount(16); // Fallback
    }
  };

  // Fetch recent users
  const getAllRecentUsers = async () => {
    try {
      // Try multiple possible endpoints
      const endpoints = [
        '/profiles/new-registrations?page=1&limit=100',
        '/users/recent?limit=100',
        '/admin/recent-users',
        '/users?sort=newest&limit=100'
      ];

      let usersResponse = null;

      for (const endpoint of endpoints) {
        try {
          const response = await getWithTokenAPI(token, endpoint);
          if (response?.statusCode === 200 || response?.success) {
            usersResponse = response;
            break;
          }
        } catch (error) {
          console.log(`Endpoint ${endpoint} failed, trying next...`);
          continue;
        }
      }

      console.log('Recent Users Response:', usersResponse);

      let usersData = [];

      if (usersResponse?.data?.users) {
        usersData = usersResponse.data.users;
      } else if (Array.isArray(usersResponse?.data)) {
        usersData = usersResponse.data;
      } else if (Array.isArray(usersResponse)) {
        usersData = usersResponse;
      }


      console.log(usersData ,"usersData")
      // Format users data
      const formattedUsers = usersData.map((user, index) => ({
        id: user.mobile_number || user.id || user.user_id || `user-${index}`,
        name: user.name || user.fullName || user.username || 'Unknown User',
        mobile: user.mobile_number || user.mobile || user.phone || 'N/A',
        registeredAt: user.registered_at || user.createdAt || user.join_date,
        joinDate: formatDate(user.registered_at || user.createdAt || user.join_date),
        daysAgo: calculateDaysAgo(user.registered_at || user.createdAt || user.join_date),
        status: user.status || 'active',
        profileImage: user.profilePhoto || user.profile_image || user.photo || null,
        userId : user.user_id
      }));

      setRecentUsers(formattedUsers);
    } catch (error) {
      console.error('Error fetching recent users:', error);
      // Set fallback recent users
      setRecentUsers([
        {
          id: 'user-1',
          name: 'John Doe',
          mobile: '+91 9876543210',
          joinDate: 'Nov 5, 2025',
          daysAgo: '2 days ago',
          status: 'active',
          profileImage: null
        },
        {
          id: 'user-2',
          name: 'Jane Smith',
          mobile: '+91 9876543211',
          joinDate: 'Nov 4, 2025',
          daysAgo: '3 days ago',
          status: 'active',
          profileImage: null
        }
      ]);
    }
  };

  // Fetch user profile data
  const fetchUserProfile = async (user_id) => {
    if (!user_id) return { name: 'Unknown User', profileImage: null };

    const endpoints = [
      `/admin/get-user-profile/${user_id}`,
      `/profile/get_all/${user_id}`,
      `/users/${user_id}`,
      `/user/${user_id}`,
      `/admin/users/${user_id}`
    ];

    for (const endpoint of endpoints) {
      try {
        const response = await getWithTokenAPI(token, endpoint);
        console.log(`User profile from ${endpoint}:`, response);

        if (response && (response.statusCode === 200 || response.success)) {
          const userData = response.data || response.user || response.profile || response;

          if (userData) {
            const userName =
              userData.basicProfile?.name ||
              userData.profile?.name ||
              userData.name ||
              userData.fullName ||
              userData.username ||
              'Unknown User';

            const profileImage =
              userData.basicProfile?.profilePhoto ||
              userData.profile?.profilePhoto ||
              userData.profilePhoto ||
              userData.photo ||
              userData.image ||
              null;

            return { name: userName, profileImage };
          }
        }
      } catch (error) {
        console.log(`Endpoint ${endpoint} failed:`, error.message);
        continue;
      }
    }

    return { name: 'Unknown User', profileImage: null };
  };

  // Fetch pending approvals
  const getApprovals = async () => {
    try {
      const response = await getWithTokenAPI(token, '/document/s3');
      console.log('Approvals Response:', response);

      if (response?.statusCode === 200 && Array.isArray(response.data)) {
        const pendingDocs = response.data.filter(doc =>
          doc.user_id &&
          doc.user_id !== '' &&
          (doc.isApproved === null || doc.isApproved === "Pending" || doc.isApproved === undefined)
        );

        console.log('Pending documents:', pendingDocs);

        setPendingApprovalsCount(pendingDocs.length);

        // Fetch user profiles for pending documents
        const userProfilesPromises = pendingDocs.map(async (doc) => {
          const userProfile = await fetchUserProfile(doc.user_id);
          return {
            ...doc,
            userName: userProfile.name,
            userProfileImage: userProfile.profileImage
          };
        });

        const pendingDocsWithUserProfiles = await Promise.all(userProfilesPromises);

        const formattedApprovals = pendingDocsWithUserProfiles.map(doc => ({
          id: doc.documentId || doc.key || `doc-${Math.random()}`,
          type: determineDocumentType(doc.key),
          user: doc.userName,
          userProfileImage: doc.userProfileImage,
          documentImage: doc.url,
          user_id: doc.user_id,
          content: getContentDescription(doc.key),
          timestamp: formatTimestamp(doc.lastModified || doc.uploaded_at || doc.createdAt),
          daysAgo: calculateDaysAgo(doc.lastModified || doc.uploaded_at || doc.createdAt),
          fileName: getFileName(doc.key),
          url: doc.url,
          documentData: doc
        }));

        console.log('Formatted Approvals:', formattedApprovals);
        setPendingApprovals(formattedApprovals);
      } else {
        // Set fallback approvals data
        setPendingApprovalsCount(63);
        setPendingApprovals(generateFallbackApprovals());
      }
    } catch (error) {
      console.error('Error fetching approvals:', error);
      // Set fallback data
      setPendingApprovalsCount(63);
      setPendingApprovals(generateFallbackApprovals());
    }
  };

  // Helper function for fallback approvals data
  const generateFallbackApprovals = () => {
    return [
      {
        id: 'doc-1',
        type: 'photo',
        user: 'Rahul Kumar',
        userProfileImage: null,
        content: 'Profile photo',
        timestamp: '2 days ago',
        daysAgo: '2 days ago',
        fileName: 'profile.jpg'
      },
      {
        id: 'doc-2',
        type: 'document',
        user: 'Priya Sharma',
        userProfileImage: null,
        content: 'Supporting document',
        timestamp: '1 day ago',
        daysAgo: '1 day ago',
        fileName: 'document.pdf'
      }
    ];
  };

  // Helper functions
  const getFileName = (key) => {
    if (!key) return 'document';
    return key.split('/').pop() || key;
  };

  const determineDocumentType = (key) => {
    if (!key) return 'document';

    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('photo') || lowerKey.includes('image') ||
      lowerKey.includes('.jpg') || lowerKey.includes('.jpeg') ||
      lowerKey.includes('.png') || lowerKey.includes('.gif') ||
      lowerKey.includes('.webp')) {
      return 'photo';
    }
    return 'document';
  };

  const getContentDescription = (key) => {
    if (!key) return 'Document upload';

    const fileName = getFileName(key);
    if (fileName.includes('profile')) return 'Profile photo';
    if (fileName.includes('gallery')) return 'Gallery photo';
    if (fileName.includes('document')) return 'Supporting document';

    return 'Document upload';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown date';

    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffTime / (1000 * 60));

      if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;

      return 'Just now';
    } catch (error) {
      return 'Unknown date';
    }
  };

  const calculateDaysAgo = (dateString) => {
    if (!dateString) return 'Unknown';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return '1 day ago';
      return `${diffDays} days ago`;
    } catch (error) {
      return 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';

    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Unknown date';
    }
  };

  const toggleUsersView = () => {
    setShowAllUsers(!showAllUsers);
  };

  const toggleApprovalsView = () => {
    setShowAllApprovals(!showAllApprovals);
  };

  useEffect(() => {
    getSummary();
    getReports();
    getApprovals();
    getAllRecentUsers();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Metric Cards Skeleton */}
        <div className="flex flex-wrap gap-4 justify-start">
          {[
            { color: 'from-blue-50 to-white border-blue-100', w: 'w-24' },
            { color: 'from-green-50 to-white border-green-100', w: 'w-28' },
            { color: 'from-purple-50 to-white border-purple-100', w: 'w-20' },
            { color: 'from-emerald-50 to-white border-emerald-100', w: 'w-28' },
            { color: 'from-indigo-50 to-white border-indigo-100', w: 'w-20' },
            { color: 'from-cyan-50 to-white border-cyan-100', w: 'w-20' },
            { color: 'from-orange-50 to-white border-orange-100', w: 'w-28' },
            { color: 'from-red-50 to-white border-red-100', w: 'w-24' },
          ].map((card, i) => (
            <div key={i} style={{ flex: "1 1 200px", minWidth: "200px", maxWidth: "300px" }}>
              <div className={`rounded-xl p-4 border-2 shadow-sm space-y-3 bg-gradient-to-br ${card.color}`}>
                <div className="flex justify-between items-center">
                  <div className={`skeleton h-4 ${card.w}`}></div>
                  <div className="skeleton h-5 w-5 rounded-full"></div>
                </div>
                <div className="skeleton h-8 w-16"></div>
              </div>
            </div>
          ))}
        </div>

        {/* List Cards Skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Users Card Skeleton */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-lg rounded-xl overflow-hidden">
            <div className="flex justify-between items-center bg-gradient-to-r from-blue-100 to-blue-200 py-4 px-5">
              <div>
                <div className="skeleton h-5 w-32 mb-2"></div>
                <div className="skeleton h-3 w-24"></div>
              </div>
              <div className="skeleton h-8 w-16 rounded-md"></div>
            </div>
            <div className="p-4 space-y-3 bg-white/50">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-blue-100 rounded-lg bg-white shadow-sm">
                  <div className="flex items-center gap-3 w-full">
                    <div className="skeleton h-12 w-12 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="skeleton h-4 w-1/3"></div>
                      <div className="skeleton h-3 w-1/2"></div>
                      <div className="skeleton h-3 w-1/4"></div>
                    </div>
                  </div>
                  <div className="skeleton h-6 w-14 rounded-full"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Pending Approvals Card Skeleton */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 shadow-lg rounded-xl overflow-hidden">
            <div className="flex justify-between items-center bg-gradient-to-r from-orange-100 to-orange-200 py-4 px-5">
              <div>
                <div className="skeleton h-5 w-40 mb-2"></div>
                <div className="skeleton h-3 w-48"></div>
              </div>
              <div className="skeleton h-8 w-16 rounded-md"></div>
            </div>
            <div className="p-4 space-y-3 bg-white/50">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-orange-100 rounded-lg bg-white shadow-sm">
                  <div className="flex items-center gap-3 w-full">
                    <div className="skeleton h-10 w-10 rounded-lg"></div>
                    <div className="flex-1 space-y-2">
                      <div className="skeleton h-4 w-1/3"></div>
                      <div className="skeleton h-3 w-1/2"></div>
                      <div className="skeleton h-3 w-1/4"></div>
                    </div>
                  </div>
                  <div className="skeleton h-6 w-14 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Use actual data or fallback values
  const data = summaryData || {
    totalUsers: 95,
    activeProfiles: 63,
    goldUsers: 2,
    monthlyRevenue: 30992,
    eliteUsers: 4,
    freeUsers: 89
  };

  const usersToShow = showAllUsers ? recentUsers : recentUsers.slice(0, 5);
  const approvalsToShow = showAllApprovals ? pendingApprovals : pendingApprovals.slice(0, 5);

  console.log(usersToShow ,"usersToShow")

  return (
    <div className="space-y-6">
      {/* <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        justifyContent: 'flex-start'
      }}>
        <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '300px' }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #f0f9ff, #ffffff)',
              border: '2px solid #bae6fd'
            }}
            onClick={handleTotalUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-blue-700"> {t("dashboard.totalUsers")}</CardTitle>
              <Users className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{data.totalUsers.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '300px' }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #f0fdf4, #ffffff)',
              border: '2px solid #bbf7d0'
            }}
            onClick={handleActiveProfilesClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-green-700">{t("dashboard.activeProfiles")}</CardTitle>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{data.activeProfiles.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '300px' }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #faf5ff, #ffffff)',
              border: '2px solid #e9d5ff'
            }}
            onClick={handleGoldUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-purple-700">{t("dashboard.goldUsers")}</CardTitle>
              <Crown className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">{data.goldUsers.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '300px' }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #ecfdf5, #ffffff)',
              border: '2px solid #a7f3d0'
            }}
            onClick={handleMonthlyRevenueClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-emerald-700">{t("dashboard.monthlyRevenue")}</CardTitle>
              <DollarSign className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">₹{data.monthlyRevenue.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '300px' }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #eef2ff, #ffffff)',
              border: '2px solid #c7d2fe'
            }}
            onClick={handleEliteUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-indigo-700">Elite Users</CardTitle>
              <Crown className="h-5 w-5 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-indigo-600">{data.eliteUsers.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '300px' }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #ecfeff, #ffffff)',
              border: '2px solid #a5f3fc'
            }}
            onClick={handleFreeUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-cyan-700">Free Users</CardTitle>
              <Users className="h-5 w-5 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-cyan-600">{data.freeUsers.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '300px' }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #fff7ed, #ffffff)',
              border: '2px solid #fdba74'
            }}
            onClick={handlePendingApprovalsClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-orange-700">Pending Approvals</CardTitle>
              <AlertTriangle className="h-5 w-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{pendingApprovalsCount}</div>
            </CardContent>
          </Card>
        </div>

        <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '300px' }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #fef2f2, #ffffff)',
              border: '2px solid #fca5a5'
            }}
            onClick={handleReportedUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-red-700">Reported Users</CardTitle>
              <XCircle className="h-5 w-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{reportCount}</div>
            </CardContent>
          </Card>
        </div>
      </div> */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          justifyContent: "flex-start",
        }}
      >
        {/* Total Users Card */}
        <div style={{ flex: "1 1 200px", minWidth: "200px", maxWidth: "300px" }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #f0f9ff, #ffffff)",
              border: "2px solid #bae6fd",
            }}
            onClick={handleTotalUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-blue-700">
                {t("dashboard.totalUsers")}
              </CardTitle>
              <Users className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {data?.totalUsers?.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Active Profiles Card */}
        <div style={{ flex: "1 1 200px", minWidth: "200px", maxWidth: "300px" }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #f0fdf4, #ffffff)",
              border: "2px solid #bbf7d0",
            }}
            onClick={handleActiveProfilesClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-green-700">
                {t("dashboard.activeProfiles")}
              </CardTitle>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {data?.activeProfiles?.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gold Users Card */}
        <div style={{ flex: "1 1 200px", minWidth: "200px", maxWidth: "300px" }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #faf5ff, #ffffff)",
              border: "2px solid #e9d5ff",
            }}
            onClick={handleGoldUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-purple-700">
                {t("dashboard.goldUsers")}
              </CardTitle>
              <Crown className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {data?.goldUsers?.toLocaleString() || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Revenue Card */}
        <div style={{ flex: "1 1 200px", minWidth: "200px", maxWidth: "300px" }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #ecfdf5, #ffffff)",
              border: "2px solid #a7f3d0",
            }}
            onClick={handleMonthlyRevenueClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-emerald-700">
                {t("dashboard.monthlyRevenue")}
              </CardTitle>
              <DollarSign className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">
                ₹{data?.monthlyRevenue?.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Elite Users Card */}
        <div style={{ flex: "1 1 200px", minWidth: "200px", maxWidth: "300px" }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #eef2ff, #ffffff)",
              border: "2px solid #c7d2fe",
            }}
            onClick={handleEliteUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-indigo-700">
                {t("dashboard.eliteUsers")}
              </CardTitle>
              <Crown className="h-5 w-5 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-indigo-600">
                {data?.eliteUsers?.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Free Users Card */}
        <div style={{ flex: "1 1 200px", minWidth: "200px", maxWidth: "300px" }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #ecfeff, #ffffff)",
              border: "2px solid #a5f3fc",
            }}
            onClick={handleFreeUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-cyan-700">
                {t("dashboard.freeUsers")}
              </CardTitle>
              <Users className="h-5 w-5 text-cyan-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-cyan-600">
                {data?.freeUsers?.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Approvals Card */}
        <div style={{ flex: "1 1 200px", minWidth: "200px", maxWidth: "300px" }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #fff7ed, #ffffff)",
              border: "2px solid #fdba74",
            }}
            onClick={handlePendingApprovalsClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-orange-700">
                {t("dashboard.pendingApprovals")}
              </CardTitle>
              <AlertTriangle className="h-5 w-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">
                {pendingApprovalsCount}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reported Users Card */}
        <div style={{ flex: "1 1 200px", minWidth: "200px", maxWidth: "300px" }}>
          <Card
            className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #fef2f2, #ffffff)",
              border: "2px solid #fca5a5",
            }}
            onClick={handleReportedUsersClick}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-semibold text-red-700">
                {t("dashboard.reportedUsers")}
              </CardTitle>
              <XCircle className="h-5 w-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{reportCount}</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Third Row - 2 Column List Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users Card */}
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-blue-100 to-blue-200 rounded-t-lg py-4">
            <div>
              <CardTitle className="text-lg text-blue-900 font-bold">{t("dashboard.recentUsers")}</CardTitle>
              <CardDescription className="text-sm text-blue-700 font-medium">
                {t("dashboard.newRegistrations", { count: recentUsers.length })}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {/* Show More Button - Navigates to User Management */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShowMoreUsers}
                className="h-8 px-3 text-xs border border-blue-300 bg-white text-blue-700 hover:bg-blue-200 font-medium cursor-pointer"
              >
                {t("dashboard.showMore")}
              </Button>
              {/* Show All Button - Navigates to User Management */}
              {/* <Button
                variant="ghost"
                size="sm"
                onClick={handleShowAllUsers}
                className="h-8 px-3 text-xs border border-blue-300 bg-blue-600 text-white hover:bg-blue-700 font-medium"
              >
                Show All
              </Button> */}
            </div>
          </CardHeader>
          <CardContent className="space-y-3 p-4">
            {usersToShow.length > 0 ? (
              usersToShow.map((user) => (
                <div key={user.id} 
                onClick={()=>{
                   navigate(`/admin/user-profile/${user.userId}`);
                }}
                className="flex items-center justify-between cursor-pointer p-3 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors bg-white shadow-sm">
                  <div className="flex items-center gap-3">
                    {user.profileImage ? (
                      <div className="relative">
                        <img
                          src={user.profileImage}
                          alt={user.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-blue-300"
                        />
                        <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-xs px-1 py-0.5 rounded-full min-w-[20px] text-center">
                          {user.daysAgo.includes('Today') ? '0d' :
                            user.daysAgo.includes('1 day') ? '1d' :
                              user.daysAgo.replace(' days ago', 'd')}
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center border-2 border-blue-300">
                          <User className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-xs px-1 py-0.5 rounded-full min-w-[20px] text-center">
                          {user.daysAgo.includes('Today') ? '0d' :
                            user.daysAgo.includes('1 day') ? '1d' :
                              user.daysAgo.replace(' days ago', 'd')}
                        </div>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-600">{user.mobile}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        Joined {user.joinDate}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="default" className="text-xs bg-green-100 text-green-800 border border-green-300 font-medium">
                      {user.status}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No recent users found</p>
              </div>
            )}

            {recentUsers.length > 5 && (
              <div className="text-center pt-2">
                <div className="text-xs text-blue-600 font-medium">
                  Showing {usersToShow.length} of {recentUsers.length} users
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Approvals Card */}
        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between bg-gradient-to-r from-orange-100 to-orange-200 rounded-t-lg py-4">
            <div>
              <CardTitle className="text-lg text-orange-900 font-bold">{t("dashboard.pendingApprovals")}</CardTitle>
              <CardDescription className="text-sm text-orange-700 font-medium">
             {t("dashboard.documentsWaiting", { count: pendingApprovalsCount })}
                {pendingApprovalsCount > 5 && (
                  
                  <span className="ml-2 text-xs">
                    {t("dashboard.showingCount", {
                      shown: approvalsToShow.length,
                      total: pendingApprovalsCount
                    })}
                  </span>
                )}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {/* Show More Button - Navigates to Content Approvals */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShowMoreApprovals}
                className="h-8 px-3 text-xs border border-orange-300 bg-white text-orange-700 hover:bg-orange-200 font-medium cursor-pointer"
              >
                {t("dashboard.showMore")}
              </Button>
            
            </div>
          </CardHeader>
          <CardContent className="space-y-3 p-4">
            {approvalsToShow.length > 0 ? (
              approvalsToShow.map((item) => (
                <div key={item.id}
                 onClick={handleShowMoreApprovals}
                 className="flex items-center justify-between p-3  cursor-pointer border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors bg-white shadow-sm">
                  <div className="flex items-center gap-3 flex-1">
                    {/* Show the actual document/image from URL */}
                    {item.url && item.type === 'photo' ? (
                      <div className="relative">
                        <img
                          src={item.url}
                          alt={item.content}
                          className="w-12 h-12 rounded-full object-cover border-2 border-orange-300"
                        />
                      </div>
                    ) : (
                      <div className="relative">
                        <FileText className="w-12 h-12 text-green-500 p-2 bg-green-50 rounded-lg border-2 border-orange-300" />
                        <div className="absolute -bottom-1 -right-1 bg-orange-500 text-white text-xs px-1 py-0.5 rounded-full min-w-[20px] text-center">
                          {item.daysAgo.includes('Today') ? '0d' :
                            item.daysAgo.includes('1 day') ? '1d' :
                              item.daysAgo.replace(' days ago', 'd')}
                        </div>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {item.userProfileImage ? (
                          <img
                            src={item.userProfileImage}
                            alt={item.user}
                            className="w-6 h-6 rounded-full object-cover border border-gray-300"
                          />
                        ) : (
                          <User className="w-4 h-4 text-gray-400" />
                        )}
                        <p className="font-semibold text-sm text-gray-900 truncate">{item.user}</p>
                      </div>
                      <p className="text-xs text-gray-600 truncate mt-1">{item.content}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        Pending for {item.daysAgo}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 border border-blue-300 font-medium">
                    {item.type}
                  </Badge>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No pending approvals</p>
                <p className="text-sm">All documents have been reviewed</p>
              </div>
            )}

            {pendingApprovals.length > 5 && (
              <div className="text-center pt-2">
                <div className="text-xs text-orange-600 font-medium">
                  Showing {approvalsToShow.length} of {pendingApprovals.length} approvals
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardSection;